import React from 'react';
import {
  Drawer,
  DrawerHeader,
  DrawerContent,
  DrawerFooter,
  DrawerCloseButton,
  Flex,
  Text,
  Button,
  Markdown,
  EditableBadge,
  Dialog,
  DialogTrigger,
  DialogFooter,
  useToast,
  InlineEditableText,
} from '@chatsdet/ui';
import { Trash } from '@phosphor-icons/react';
import { formatDistanceToNow } from 'date-fns';
import { getBoardManifest, boardHasFeature, type BoardType } from '@chatsdet/cards-plugins';
import { useCard, useUpdateCard, useDeleteCard, type Card } from '../hooks/useCards';
import { SectionLabel } from './SectionLabel';
import { ChecklistPanel } from './ChecklistPanel';
import { CardCommentsPanel } from './CardCommentsPanel';

interface Props {
  cardId: string;
  boardType: BoardType;
  onClose: () => void;
  /**
   * Optional render-prop for board-specific detail fields (read/write `card.details`).
   * `commit` (when provided) persists the current details — call it on blur for
   * inline auto-save.
   */
  renderDetailFields?: (
    details: Record<string, unknown>,
    setDetails: (next: Record<string, unknown>) => void,
    commit?: () => void
  ) => React.ReactNode;
  renderExtraSections?: (card: Card) => React.ReactNode;
}

export function CardDrawer({
  cardId,
  boardType,
  onClose,
  renderDetailFields,
  renderExtraSections,
}: Props) {
  const manifest = getBoardManifest(boardType);
  const { data: card } = useCard(cardId);
  const update = useUpdateCard(boardType);
  const remove = useDeleteCard(boardType);
  const { toast } = useToast();

  const [details, setDetails] = React.useState<Record<string, unknown>>({});
  const [deleteConfirmOpen, setDeleteConfirmOpen] = React.useState(false);

  React.useEffect(() => {
    if (card) setDetails(card.details);
  }, [card?.id]);

  if (!card) {
    return (
      <Drawer>
        <Flex justify="center" align="center" className="p-8">
          <Text>Loading…</Text>
        </Flex>
      </Drawer>
    );
  }

  const entityLabel = manifest.label.replace(/s$/, '');
  const stageOptions = manifest.stages.map((s) => ({ id: s.value, label: s.label }));

  // Persist the latest local `details` (called on blur of detail fields).
  const commitDetails = () => update.mutate({ id: card.id, details });

  const handleConfirmDelete = async () => {
    try {
      await remove.mutateAsync(card.id);
      toast.success('Deleted');
      setDeleteConfirmOpen(false);
      onClose();
    } catch {
      toast.error('Delete failed');
    }
  };

  const stageLabel = manifest.stages.find((s) => s.value === card.stage)?.label ?? card.stage;

  return (
    <Drawer hideCloseButton>
      <DrawerHeader className="space-y-3">
        {/* Row 1: editable stage + close */}
        <Flex align="center" justify="between" gap="md">
          <Flex align="center" gap="sm" wrap>
            <EditableBadge
              value={card.stage}
              options={stageOptions}
              onChange={(stage) => update.mutate({ id: card.id, stage })}
              variant="secondary"
              size="sm"
              isLoading={update.isPending}
              aria-label={`Change stage for ${entityLabel}`}
            />
          </Flex>
          <DrawerCloseButton />
        </Flex>

        {/* Title — double-click to edit inline */}
        <InlineEditableText
          value={card.title}
          onSave={(title) => update.mutate({ id: card.id, title })}
          ariaLabel={`${entityLabel} title`}
          placeholder={`Untitled ${entityLabel.toLowerCase()}`}
          className="text-xl font-semibold leading-tight"
          inputClassName="text-xl font-semibold leading-tight"
        />

        {/* Metadata */}
        <Text size="sm" className="text-muted-foreground">
          Updated {formatDistanceToNow(new Date(card.updatedAt))} ago
        </Text>
      </DrawerHeader>

      <DrawerContent className="space-y-7">
        {/* Description — clean inline body text, double-click to edit */}
        <InlineEditableText
          value={card.description ?? ''}
          multiline
          onSave={(description) => update.mutate({ id: card.id, description })}
          ariaLabel={`${entityLabel} description`}
          placeholder="Add a description…"
          className="text-[15px] leading-relaxed text-foreground"
          renderDisplay={(value) => (
            <Markdown
              compact
              className="text-[15px] leading-relaxed [&_img]:max-w-full [&_img]:rounded-md [&_img]:my-2"
            >
              {value}
            </Markdown>
          )}
        />

        {/* Board-specific detail fields (auto-saved on blur) */}
        {renderDetailFields && (
          <section className="space-y-3 border-t border-border pt-6">
            <SectionLabel>Details</SectionLabel>
            {renderDetailFields(details, setDetails, commitDetails)}
          </section>
        )}

        {renderExtraSections?.(card)}

        {boardHasFeature(boardType, 'checklists') && (
          <section className="space-y-3 border-t border-border pt-6">
            <SectionLabel>Checklist</SectionLabel>
            <ChecklistPanel cardId={card.id} />
          </section>
        )}

        {boardHasFeature(boardType, 'comments') && (
          <section className="space-y-3 border-t border-border pt-6">
            <SectionLabel>Comments</SectionLabel>
            <CardCommentsPanel cardId={card.id} className="space-y-4" />
          </section>
        )}
      </DrawerContent>

      <DrawerFooter className="shadow-[0_-2px_12px_-6px_rgba(0,0,0,0.12)]">
        <Flex align="center" justify="between" gap="md" className="w-full">
          <Text size="xs" className="text-muted-foreground">
            {stageLabel}
          </Text>
          <Button
            variant="outline"
            size="sm"
            iconLeft={<Trash size={14} />}
            onPress={() => setDeleteConfirmOpen(true)}
            className="text-destructive hover:text-destructive"
          >
            Delete {entityLabel.toLowerCase()}
          </Button>
        </Flex>
      </DrawerFooter>

      <DialogTrigger isOpen={deleteConfirmOpen} onOpenChange={setDeleteConfirmOpen}>
        <span style={{ display: 'none' }} />
        <Dialog title={`Delete ${entityLabel.toLowerCase()}`}>
          <Text>
            Are you sure you want to delete <strong>{card.title}</strong>? This action cannot be
            undone.
          </Text>
          <DialogFooter>
            <Flex gap="sm">
              <Button variant="outline" onPress={() => setDeleteConfirmOpen(false)}>
                Cancel
              </Button>
              <Button
                variant="destructive"
                iconLeft={<Trash size={14} weight="bold" />}
                onPress={handleConfirmDelete}
                isDisabled={remove.isPending}
              >
                {remove.isPending ? 'Deleting…' : 'Delete'}
              </Button>
            </Flex>
          </DialogFooter>
        </Dialog>
      </DialogTrigger>
    </Drawer>
  );
}
